import { useState } from "react";
import { Button } from "../../components/Button";
import { Card } from "../../components/Card";
import { Chip } from "../../components/Chip";
import { MiniBar } from "../../components/MiniBar";
import { api } from "../../lib/api";
import type { SubscriptionDTO, UserDTO } from "@shared/api";

interface Props {
  sub: SubscriptionDTO;
  assignedUsers: UserDTO[];
  onChanged: () => void | Promise<void>;
}

const ROLES = ["Admin", "Solutions", "Developer", "Compliance", "Viewer"] as const;

export function SeatsManager({ sub, assignedUsers, onChanged }: Props) {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function changeSeats(delta: number) {
    const next = Math.max(0, sub.seatsTotal + delta);
    if (next === sub.seatsTotal) return;
    setPending(true);
    setError(null);
    try {
      await api.subscriptions.update(sub.id, { seatsTotal: next });
      await onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setPending(false);
    }
  }

  async function removeUser(userId: string) {
    setPending(true);
    setError(null);
    try {
      await api.users.removeSeat(userId, sub.id);
      await onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="bbc-detail-grid bbc-detail-grid-1-2">
      <Card title="Seat allocation">
        {error && <div className="bbc-error">{error}</div>}
        <div className="bbc-seat-summary">
          <div className="bbc-seat-num mono">
            {sub.seatsUsed}
            <span className="dim">/{sub.seatsTotal}</span>
          </div>
          <div>
            <div className="bbc-seat-label">seats assigned</div>
            <MiniBar
              value={sub.seatsUsed}
              max={Math.max(1, sub.seatsTotal)}
              tone={
                sub.seatsTotal > 0 && sub.seatsUsed / sub.seatsTotal > 0.85
                  ? "warn"
                  : "default"
              }
            />
          </div>
        </div>
        <div className="bbc-seat-actions">
          <Button
            variant="secondary"
            size="sm"
            disabled={pending || sub.seatsTotal <= sub.seatsUsed}
            onClick={() => changeSeats(-1)}
          >
            − Remove seat
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={pending}
            onClick={() => changeSeats(5)}
          >
            + Add 5 seats
          </Button>
        </div>
        <div className="bbc-seat-breakdown">
          <h5>By role</h5>
          {ROLES.map((role) => {
            const count = assignedUsers.filter((u) => u.role === role).length;
            return (
              <div key={role} className="bbc-seat-row">
                <span>{role}</span>
                <span className="mono">{count}</span>
              </div>
            );
          })}
        </div>
      </Card>

      <Card
        title={`Users assigned to this subscription · ${assignedUsers.length}`}
        action={
          <Button variant="primary" size="sm">
            + Assign user
          </Button>
        }
        padded={false}
      >
        <table className="bbc-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Role</th>
              <th>Other subscriptions</th>
              <th>Last active</th>
              <th aria-hidden="true" />
            </tr>
          </thead>
          <tbody>
            {assignedUsers.map((u) => (
              <tr key={u.id}>
                <td>
                  <div className="bbc-cell-user">
                    <span className="bbc-avatar bbc-avatar-sm">
                      {u.initials}
                    </span>
                    <div>
                      <div>{u.name}</div>
                      <div className="bbc-cell-sub">{u.email}</div>
                    </div>
                  </div>
                </td>
                <td>
                  <Chip tone="muted">{u.role}</Chip>
                </td>
                <td className="num mono">
                  {Math.max(0, u.seats.length - 1)}
                </td>
                <td className="bbc-cell-sub">{u.lastActive}</td>
                <td>
                  <button
                    type="button"
                    className="bbc-text-btn-sm"
                    onClick={() => removeUser(u.id)}
                    disabled={pending}
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {assignedUsers.length === 0 && (
          <div className="bbc-empty">
            No users assigned to this subscription.
          </div>
        )}
      </Card>
    </div>
  );
}
